import PropTypes from "prop-types";
import React, { useMemo, useState, useEffect, useCallback } from "react";
import {
  Dropdown,
  DropdownMenu,
  DropdownItem,
  DropdownToggle,
  Tooltip,
} from "reactstrap";
import endpoints from "../../api/endpoints";
import { get, post, del } from "../../api/axios";
import HorizontalScroll from "../../components/Common/HorizontalScroll";
import RestaurantDetailModal from "./RestaurantDetailModal";
import EmptyState from "../../components/Common/EmptyState";
import SecondaryButton from "../../components/Common/SecondaryButton";
import "../../assets/scss/custom/components/explore-partners.scss";
import filters from "../../assets/images/user-dashboard/filters.svg";
import search from "../../assets/images/user-dashboard/search.svg";
import heart from "../../assets/images/user-dashboard/heart.svg";
import star from "../../assets/images/user-dashboard/star.svg";
import map from "../../assets/images/user-dashboard/map.svg";
import heart2 from "../../assets/images/user-dashboard/heart-3.svg";
import fallbackImage from "../../assets/images/user-dashboard/image.svg";
import heart3 from "../../assets/images/user-dashboard/heart-2.svg";
import redeem from "../../assets/images/user-dashboard/redeem.svg";

/* -------------------------------------------------------------------------
 * 1. Header — title + search + heart/filter actions
 * ---------------------------------------------------------------------- */

const ExploreHeader = ({
  title,
  searchValue,
  onSearchChange,
  favoritesOnly,
  onToggleFavoritesOnly,
  onOpenFilters,
  sortBy,
  onSortChange,
  visitedFilter,
  onVisitedFilterChange,
}) => {
  const [filterDropdownOpen, setFilterDropdownOpen] = useState(false);

  const toggleFilterDropdown = () =>
    setFilterDropdownOpen((prevState) => !prevState);

  const handleDropdownItemClick = (e, callback, value) => {
    e.preventDefault();
    e.stopPropagation();
    callback(value);
    setFilterDropdownOpen(false);
  };

  return (
    <div className="explore-header">
      <h2 className="explore-header__title">{title}</h2>

      <div className="explore-header__icon-actions">
        <button
          type="button"
          className={`explore-icon-btn ${
            favoritesOnly ? "explore-icon-btn--active" : ""
          }`}
          onClick={onToggleFavoritesOnly}
          aria-pressed={favoritesOnly}
          aria-label="Show favorites only"
          title="Show favorites only"
        >
          <img src={favoritesOnly ? heart2 : heart} alt="Heart" />
        </button>

        <Dropdown isOpen={filterDropdownOpen} toggle={() => {}}>
          <DropdownToggle
            tag="button"
            className="explore-icon-btn"
            onClick={toggleFilterDropdown}
          >
            <img src={filters} alt="Filters" />
          </DropdownToggle>
          <DropdownMenu className="explore-filter-dropdown">
            <DropdownItem header>Sort by</DropdownItem>
            <DropdownItem
              onClick={(e) =>
                handleDropdownItemClick(e, onSortChange, "alphabetically")
              }
              active={sortBy === "alphabetically"}
            >
              Alphabetical A to Z
            </DropdownItem>
            <DropdownItem
              onClick={(e) =>
                handleDropdownItemClick(e, onSortChange, "alphabetically_desc")
              }
              active={sortBy === "alphabetically_desc"}
            >
              Alphabetical Z to A
            </DropdownItem>
            <DropdownItem
              onClick={(e) =>
                handleDropdownItemClick(e, onSortChange, "rating_desc")
              }
              active={sortBy === "rating_desc"}
            >
              Highest rated
            </DropdownItem>
            <DropdownItem
              onClick={(e) =>
                handleDropdownItemClick(e, onSortChange, "rating_asc")
              }
              active={sortBy === "rating_asc"}
            >
              Lowest rated
            </DropdownItem>
            <DropdownItem divider />
            <DropdownItem header>Visited by</DropdownItem>
            <DropdownItem
              onClick={(e) =>
                handleDropdownItemClick(e, onVisitedFilterChange, "all")
              }
            >
              All
            </DropdownItem>
            <DropdownItem
              onClick={(e) =>
                handleDropdownItemClick(e, onVisitedFilterChange, "visited")
              }
              active={visitedFilter === "visited"}
            >
              Visited
            </DropdownItem>
            <DropdownItem
              onClick={(e) =>
                handleDropdownItemClick(e, onVisitedFilterChange, "not_visited")
              }
              active={visitedFilter === "not_visited"}
            >
              Not Visited
            </DropdownItem>
          </DropdownMenu>
        </Dropdown>
      </div>

      <div className="explore-search">
        <input
          type="text"
          value={searchValue}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder="Search burgers, brunch, or happy hour..."
          aria-label="Search partner restaurants"
        />
        <img src={search} alt="Search" className="explore-search__icon" />
      </div>
    </div>
  );
};

ExploreHeader.propTypes = {
  title: PropTypes.string,
  searchValue: PropTypes.string.isRequired,
  onSearchChange: PropTypes.func.isRequired,
  favoritesOnly: PropTypes.bool.isRequired,
  onToggleFavoritesOnly: PropTypes.func.isRequired,
  onOpenFilters: PropTypes.func,
  sortBy: PropTypes.string.isRequired,
  onSortChange: PropTypes.func.isRequired,
  visitedFilter: PropTypes.string,
  onVisitedFilterChange: PropTypes.func,
};

ExploreHeader.defaultProps = {
  title: "Explore Our Restaurants",
  onOpenFilters: () => {},
};

/* -------------------------------------------------------------------------
 * 2. Category filter bar — horizontal scrolling pills
 * ---------------------------------------------------------------------- */

const CategoryFilterBar = ({
  categories,
  activeCategory,
  onSelect,
  hasMore,
  loadingMore,
  onLoadMore,
}) => {
  const [imageErrors, setImageErrors] = useState({});
  const sentinelRef = React.useRef(null);

  const handleImageError = (categoryId) => {
    setImageErrors((prev) => ({ ...prev, [categoryId]: true }));
  };

  // Intersection Observer for horizontal infinite scroll
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel || !hasMore || loadingMore) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          onLoadMore();
        }
      },
      { threshold: 0.1, rootMargin: "100px" },
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, loadingMore, onLoadMore]);

  return (
    <HorizontalScroll className="category-filters">
      <div
        role="tablist"
        aria-label="Filter by category"
        style={{ display: "flex", gap: "8px" }}
      >
        {categories.map((category) => {
          const isActive = category.id === activeCategory;
          const isImageUrl = category.icon && category.icon.startsWith("http");
          const hasImageError = imageErrors[category.id];

          return (
            <button
              key={category.id}
              type="button"
              role="tab"
              aria-selected={isActive}
              className={`category-chip ${isActive ? "category-chip--active" : ""}`}
              onClick={() => onSelect(category.id)}
            >
              <span className="category-chip__icon">
                {isImageUrl && !hasImageError ? (
                  <img
                    src={category.icon}
                    alt={category.label}
                    onError={() => handleImageError(category.id)}
                  />
                ) : category.icon && !isImageUrl ? (
                  <i className={`mdi ${category.icon}`} />
                ) : (
                  <i className="mdi mdi-food" />
                )}
              </span>
              {category.label}
            </button>
          );
        })}
        {hasMore && (
          <div
            ref={sentinelRef}
            style={{ width: "1px", height: "1px", flexShrink: 0 }}
            aria-hidden="true"
          />
        )}
        {loadingMore && (
          <>
            {[...Array(20)].map((_, index) => (
              <div
                key={`skeleton-${index}`}
                className="category-chip category-chip--skeleton skeleton"
                style={{
                  minWidth: "80px",
                  height: "40px",
                }}
              />
            ))}
          </>
        )}
      </div>
    </HorizontalScroll>
  );
};

CategoryFilterBar.propTypes = {
  categories: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      icon: PropTypes.string,
      emoji: PropTypes.string,
    }),
  ).isRequired,
  activeCategory: PropTypes.string.isRequired,
  onSelect: PropTypes.func.isRequired,
  hasMore: PropTypes.bool,
  loadingMore: PropTypes.bool,
  onLoadMore: PropTypes.func,
};

/* -------------------------------------------------------------------------
 * 3. Restaurant grid — wraps responsively, scrolls vertically
 * ---------------------------------------------------------------------- */

const RestaurantCard = React.memo(
  ({ restaurant, onToggleLike, onRedeem, onSelect, loadingLikes }) => {
    const {
      id,
      name,
      image,
      badge,
      rating,
      cuisine,
      distance,
      liked,
      categories,
    } = restaurant;
    const [imgSrc, setImgSrc] = useState(image);
    const [tooltipOpen, setTooltipOpen] = useState(false);

    const toggleTooltip = () => setTooltipOpen(!tooltipOpen);

    const hasMoreCategories = categories && categories.length > 1;
    const additionalCount = hasMoreCategories ? categories.length - 1 : 0;
    const allCategories =
      categories && categories.length > 0 ? categories.join(", ") : cuisine;

    const isLoadingLike = loadingLikes?.[id] || false;

    const handleImageError = () => {
      if (imgSrc !== fallbackImage) {
        setImgSrc(fallbackImage);
      }
    };

    return (
      <div
        className="restaurant-card"
        onClick={() => onSelect(id)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") onSelect(id);
        }}
      >
        <div className="restaurant-card__media">
          <img
            src={imgSrc}
            alt={name}
            loading="lazy"
            onError={handleImageError}
          />

          <button
            type="button"
            className={`restaurant-card__like-btn ${isLoadingLike ? "restaurant-card__like-btn--loading" : ""}`}
            onClick={(e) => {
              e.stopPropagation();
              onToggleLike(id);
            }}
            aria-pressed={liked}
            aria-label={
              liked
                ? `Remove ${name} from favorites`
                : `Save ${name} to favorites`
            }
            disabled={isLoadingLike}
          >
            {isLoadingLike ? (
              <span className="restaurant-card__like-btn__spinner" />
            ) : (
              <img
                src={liked ? heart2 : heart3}
                alt={liked ? "Liked" : "Not liked"}
              />
            )}
          </button>

          {badge && (
            <span
              className={`restaurant-card__badge restaurant-card__badge--${badge.variant}`}
            >
              {badge.label}
            </span>
          )}

          <button
            type="button"
            className="restaurant-card__redeem"
            onClick={(e) => {
              e.stopPropagation();
              onRedeem(id);
            }}
            aria-label={`Redeem offer at ${name}`}
          >
            <img src={redeem} alt="Redeem" />
            Redeem
          </button>
        </div>

        <div className="restaurant-card__body">
          <div className="restaurant-card__top-row">
            <p className="restaurant-card__name">{name}</p>
            <span className="restaurant-card__rating">
              <img src={star} alt="Star" />
              {rating.toFixed(1)}
            </span>
          </div>
          <div className="restaurant-card__meta-row">
            <div className="restaurant-card__cuisine-wrapper">
              <span className="restaurant-card__cuisine">{cuisine}</span>
              {hasMoreCategories && (
                <>
                  <span
                    className="restaurant-card__more-categories"
                    id={`category-tooltip-${id}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleTooltip();
                    }}
                  >
                    +{additionalCount} more
                  </span>
                  <Tooltip
                    placement="top"
                    isOpen={tooltipOpen}
                    target={`category-tooltip-${id}`}
                    toggle={toggleTooltip}
                    transition={{ timeout: 0 }}
                  >
                    {allCategories}
                  </Tooltip>
                </>
              )}
            </div>
            {distance && (
              <span className="restaurant-card__distance">
                <img src={map} alt="Map" />
                {distance}
              </span>
            )}
          </div>
        </div>
      </div>
    );
  },
);

RestaurantCard.propTypes = {
  restaurant: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    name: PropTypes.string.isRequired,
    image: PropTypes.string.isRequired,
    badge: PropTypes.shape({
      label: PropTypes.string,
      variant: PropTypes.oneOf(["popular", "new", "trending", "cashback"]),
    }),
    rating: PropTypes.number.isRequired,
    cuisine: PropTypes.string.isRequired,
    distance: PropTypes.string,
    liked: PropTypes.bool,
    categories: PropTypes.arrayOf(PropTypes.string),
  }).isRequired,
  onToggleLike: PropTypes.func.isRequired,
  onRedeem: PropTypes.func.isRequired,
  onSelect: PropTypes.func.isRequired,
  loadingLikes: PropTypes.object,
};

const RestaurantGrid = React.memo(
  ({
    restaurants,
    loading,
    loadingMore,
    onToggleLike,
    onRedeem,
    onSelectRestaurant,
    loadingLikes,
  }) => {
    if (loading) {
      return (
        <div className="restaurant-grid">
          {[...Array(6)].map((_, index) => (
            <div
              key={index}
              className="restaurant-card restaurant-card--skeleton"
            >
              <div className="restaurant-card__media">
                <div className="skeleton skeleton--image" />
              </div>
              <div className="restaurant-card__body">
                <div className="restaurant-card__top-row">
                  <div className="skeleton skeleton--name" />
                  <div className="skeleton skeleton--rating" />
                </div>
                <div className="restaurant-card__meta-row">
                  <div className="skeleton skeleton--cuisine" />
                  <div className="skeleton skeleton--distance" />
                </div>
              </div>
            </div>
          ))}
        </div>
      );
    }

    if (!restaurants || restaurants.length === 0) {
      return (
        <div className="restaurant-grid restaurant-grid--empty">
          <EmptyState
            title="No restaurants found"
            message="No partner restaurants match your search just yet. Try adjusting your filters or search terms."
          />
        </div>
      );
    }

    return (
      <>
        <div className="restaurant-grid">
          {restaurants.map((restaurant) => (
            <RestaurantCard
              key={restaurant.id}
              restaurant={restaurant}
              onToggleLike={onToggleLike}
              onRedeem={onRedeem}
              onSelect={onSelectRestaurant}
              loadingLikes={loadingLikes}
            />
          ))}
        </div>
        {loadingMore && (
          <div className="restaurant-grid">
            {[...Array(4)].map((_, index) => (
              <div
                key={`loading-${index}`}
                className="restaurant-card restaurant-card--skeleton"
              >
                <div className="restaurant-card__media">
                  <div className="skeleton skeleton--image" />
                </div>
                <div className="restaurant-card__body">
                  <div className="restaurant-card__top-row">
                    <div className="skeleton skeleton--name" />
                    <div className="skeleton skeleton--rating" />
                  </div>
                  <div className="restaurant-card__meta-row">
                    <div className="skeleton skeleton--cuisine" />
                    <div className="skeleton skeleton--distance" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </>
    );
  },
);

RestaurantGrid.propTypes = {
  restaurants: PropTypes.array.isRequired,
  loading: PropTypes.bool,
  loadingMore: PropTypes.bool,
  onToggleLike: PropTypes.func.isRequired,
  onRedeem: PropTypes.func.isRequired,
  onSelectRestaurant: PropTypes.func.isRequired,
  loadingLikes: PropTypes.object,
};

/* -------------------------------------------------------------------------
 * Main composed section
 * ---------------------------------------------------------------------- */

const ExplorePartnersSection = ({
  title,
  categories,
  restaurants: initialRestaurants,
  loading,
  loadingMore,
  hasMore,
  totalRestaurants,
  onLoadMore,
  onSelectRestaurant,
  onRedeemRestaurant,
  onToggleLike,
  onRatingChanged,
  onOpenFilters,
  activeCategory,
  onCategoryChange,
  searchValue,
  onSearchChange,
  sortBy,
  onSortChange,
  categoriesHasMore,
  categoriesLoadingMore,
  onLoadMoreCategories,
  visitedFilter,
  onVisitedFilterChange,
}) => {
  const [favoritesOnly, setFavoritesOnly] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedRestaurant, setSelectedRestaurant] = useState(null);
  const [loadingLikes, setLoadingLikes] = useState({});
  const [userRatings, setUserRatings] = useState({});
  const [isScrolling, setIsScrolling] = useState(false);

  const formatVisitDate = (dateValue) => {
    if (!dateValue) return null;
    const date = new Date(dateValue);
    if (Number.isNaN(date.getTime())) return null;

    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const getTransactionDate = (transaction) =>
    transaction?.transacted_at ||
    transaction?.posted_at ||
    transaction?.visited_at ||
    transaction?.created_at ||
    null;

  const getLastVisitDate = useCallback((transactions = []) => {
    if (!Array.isArray(transactions)) return null;

    return (
      transactions.reduce((latest, transaction) => {
        const dateValue = getTransactionDate(transaction);
        if (!dateValue) return latest;

        const timestamp = new Date(dateValue).getTime();
        if (Number.isNaN(timestamp)) return latest;

        return !latest || timestamp > latest.timestamp
          ? { timestamp, value: dateValue }
          : latest;
      }, null)?.value || null
    );
  }, []);
  const scrollRef = React.useRef(null);
  const sentinelRef = React.useRef(null);
  const [showLoadMoreButton, setShowLoadMoreButton] = useState(false);

  // Check if content fits in viewport (no scroll needed)
  useEffect(() => {
    const checkIfFitsInViewport = () => {
      if (scrollRef.current) {
        const { scrollHeight, clientHeight } = scrollRef.current;
        // If content height is less than or equal to viewport, show load more button
        setShowLoadMoreButton(
          scrollHeight <= clientHeight && hasMore && !loadingMore,
        );
      }
    };

    checkIfFitsInViewport();

    // Re-check when restaurants or loading state changes
    const resizeObserver = new ResizeObserver(checkIfFitsInViewport);
    if (scrollRef.current) {
      resizeObserver.observe(scrollRef.current);
    }

    return () => resizeObserver.disconnect();
  }, [initialRestaurants, hasMore, loadingMore]);

  // Intersection Observer for infinite scroll - works on all screen sizes
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel || !hasMore || loadingMore) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (
          entries[0].isIntersecting &&
          hasMore &&
          !loadingMore &&
          !isScrolling
        ) {
          setIsScrolling(true);
          onLoadMore();
          // Reset scrolling flag after a short delay to prevent rapid calls
          setTimeout(() => setIsScrolling(false), 500);
        }
      },
      { threshold: 0.1, rootMargin: "200px" },
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, loadingMore, isScrolling, onLoadMore]);

  // Scroll-based infinite scroll as fallback
  const handleScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    const scrollPercentage = (scrollTop + clientHeight) / scrollHeight;

    // Load more when scrolled to 90% of the bottom
    if (hasMore && onLoadMore && scrollPercentage >= 0.9 && !isScrolling) {
      setIsScrolling(true);
      onLoadMore();
      setTimeout(() => setIsScrolling(false), 500);
    }
  };

  // Fetch restaurant details when selected
  const fetchRestaurantDetails = useCallback(
    async (restaurantId) => {
      try {
        const response = await get(
          `${endpoints.restaurantDetails}/${restaurantId}`,
        );
        const apiData = response.data.data; // Data is nested under response.data.data

        // Get today's day name in lowercase
        const today = new Date()
          .toLocaleDateString("en-US", { weekday: "long" })
          .toLowerCase();

        // Find today's hours from listing_time
        const todayHours = apiData.listing_time?.find(
          (item) => item.day.toLowerCase() === today,
        );

        // Handle case where listing_time is empty or null
        let formattedHours;
        if (!apiData.listing_time || apiData.listing_time.length === 0) {
          formattedHours = apiData.open_24_hours ? "Open 24 hours" : "Closed";
        }

        // Helper function to convert 24-hour time to AM/PM format
        const formatTimeToAMPM = (timeString) => {
          if (
            !timeString ||
            timeString === "null-null" ||
            timeString.includes("null")
          ) {
            return "Closed";
          }

          // Check if it's a time range (e.g., "11:00-21:00")
          if (timeString.includes("-")) {
            const [startTime, endTime] = timeString.split("-");

            const convertSingleTime = (time) => {
              if (!time || time === "null") return "Closed";
              const [hours, minutes] = time.split(":");
              const hour = parseInt(hours, 10);
              if (isNaN(hour)) return "Closed";
              const ampm = hour >= 12 ? "PM" : "AM";
              const hour12 = hour % 12 || 12;
              return `${hour12}:${minutes} ${ampm}`;
            };

            const formattedStart = convertSingleTime(startTime);
            const formattedEnd = convertSingleTime(endTime);

            if (formattedStart === "Closed" || formattedEnd === "Closed") {
              return "Closed";
            }

            return `${formattedStart} - ${formattedEnd}`;
          }

          // Handle single time format
          const [hours, minutes] = timeString.split(":");
          const hour = parseInt(hours, 10);
          if (isNaN(hour)) return "Closed";
          const ampm = hour >= 12 ? "PM" : "AM";
          const hour12 = hour % 12 || 12;
          return `${hour12}:${minutes} ${ampm}`;
        };

        formattedHours = formatTimeToAMPM(todayHours?.hours);

        // Transform categories to array of titles
        const categoriesList =
          apiData.categories?.map((cat) => cat.title) || [];

        // Transform secret menus to menu items format
        const menuItems =
          apiData.secret_menus?.map((menu) => ({
            id: menu.id,
            name: menu.name,
            description: menu.description,
            price: 0, // Secret menus don't have price in API, default to 0
            image: menu.media?.[0]?.media_path || null,
          })) || [];

        // Transform API data to modal format
        const firstCategory = apiData.categories?.[0];

        // Handle visit history - only show if is_visited is true
        const customerTransactions = Array.isArray(apiData.transections)
          ? apiData.transections
          : [];
        const visitCount =
          customerTransactions.length || Number(apiData.is_visited || 0);
        const lastVisitedLabel = formatVisitDate(
          getLastVisitDate(customerTransactions),
        );
        let visitHistory = null;
        if (visitCount > 0 || apiData.is_visited) {
          visitHistory = {
            lastVisitedLabel: lastVisitedLabel || "-",
            visitCount,
          };
        }

        const transformedData = {
          id: String(apiData.id),
          name: apiData.title,
          image:
            apiData.banner_url ||
            "https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?auto=format&fit=crop&w=600&q=80",
          category: firstCategory?.title || "Restaurant",
          categories: categoriesList,
          tag: apiData.premium ? "Premium" : null,
          rating: apiData.resturant_rating?.rating || 0,
          userRating: userRatings[apiData.id] || 0, // Include user's rating from userRatings state
          distance: null, // Will be calculated in modal
          latitude: apiData.latitude || apiData.address?.latitude,
          longitude: apiData.longitude || apiData.address?.longitude,
          description: apiData.overview || "No description available",
          hoursLabel: "Today",
          hoursValue: formattedHours || "Closed",
          visitHistory,
          is_visited: apiData.is_visited,
          address: apiData.address
            ? `${apiData.address.address}, ${apiData.address.city}, ${apiData.address.state} ${apiData.address.zipcode}`
            : null,
          menuItems,
          redeemLabel: "Redeem Voucher",
        };

        setSelectedRestaurant(transformedData);
      } catch (err) {
        // Fallback to restaurant from list if API fails
        const fallbackRestaurant = initialRestaurants.find(
          (r) => r.id === restaurantId,
        );
        if (fallbackRestaurant) {
          setSelectedRestaurant({
            id: fallbackRestaurant.id,
            name: fallbackRestaurant.name,
            image: fallbackRestaurant.image,
            category: fallbackRestaurant.cuisine,
            categories: fallbackRestaurant.categories || [
              fallbackRestaurant.cuisine,
            ],
            tag: fallbackRestaurant.badge?.label || null,
            rating: fallbackRestaurant.rating,
            userRating: userRatings[restaurantId] || 0,
            distance: fallbackRestaurant.distance,
            description: "No description available",
            hoursLabel: "Today",
            hoursValue: "Closed",
            menuSectionLabel: "Menu",
            menuItems: [],
            redeemLabel: "Redeem Voucher",
          });
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [initialRestaurants, userRatings],
  );

  const handleRedeemFromCard = useCallback(
    (restaurantId) => {
      if (onRedeemRestaurant) {
        const restaurant = initialRestaurants.find(
          (r) => r.id === restaurantId,
        );
        if (restaurant) {
          onRedeemRestaurant(restaurant);
        }
      }
    },
    [initialRestaurants, onRedeemRestaurant],
  );

  const handleRedeemFromDetailModal = useCallback(
    (restaurant) => {
      if (onRedeemRestaurant) {
        onRedeemRestaurant(restaurant);
      }
    },
    [onRedeemRestaurant],
  );

  const handleToggleLike = useCallback(
    async (id) => {
      const restaurant = initialRestaurants.find((r) => r.id === id);
      if (!restaurant) return;

      // Set loading state for this specific restaurant
      setLoadingLikes((prev) => ({ ...prev, [id]: true }));

      try {
        const newLikedStatus = !restaurant.liked;
        if (restaurant.liked) {
          // Remove from wishlist (DELETE)
          await del(`${endpoints.wishlist}/${id}`);
        } else {
          // Add to wishlist (POST)
          await post(endpoints.wishlist, { vendor_listing_id: id });
        }
        // Update parent state
        if (onToggleLike) {
          onToggleLike(id, newLikedStatus);
        }
      } catch (err) {
        // Error handling - parent will handle the toast
      } finally {
        // Clear loading state for this restaurant
        setLoadingLikes((prev) => ({ ...prev, [id]: false }));
      }
    },
    [initialRestaurants, onToggleLike],
  );

  const handleSelectRestaurant = useCallback(
    (id) => {
      fetchRestaurantDetails(id);
      setIsModalOpen(true);
      if (onSelectRestaurant) {
        onSelectRestaurant(id);
      }
    },
    [fetchRestaurantDetails, onSelectRestaurant],
  );

  const toggleModal = useCallback(() => {
    setIsModalOpen((prev) => !prev);
  }, []);

  const handleRatingChange = useCallback(
    (restaurantId, newRating) => {
      setUserRatings((prev) => ({
        ...prev,
        [restaurantId]: newRating,
      }));
      // Call parent callback to reload restaurants with updated average ratings
      if (onRatingChanged) {
        onRatingChanged();
      }
    },
    [onRatingChanged],
  );

  const handleRefreshRestaurant = useCallback(async (restaurantId) => {
    try {
      const response = await get(
        `${endpoints.restaurantDetails}/${restaurantId}`,
      );
      const apiData = response.data.data;

      setUserRatings((prev) => ({
        ...prev,
        [restaurantId]: apiData.rating || prev[restaurantId],
      }));
    } catch (error) {
      console.error("Failed to refresh restaurant data:", error);
    }
  }, []);

  const visibleRestaurants = useMemo(() => {
    return initialRestaurants
      .filter((r) => {
        // Filter by favorites
        if (favoritesOnly && !r.liked) return false;

        // Filter by visited status (client-side workaround since API doesn't support it)
        // Handle both boolean and numeric values for is_visited
        const isVisited = r.is_visited === true || r.is_visited === 1;
        if (visitedFilter === "visited" && !isVisited) return false;
        if (visitedFilter === "not_visited" && isVisited) return false;

        // Note: Category and search filtering are handled server-side by the API
        // We only do client-side filtering for favorites-only and visited status

        return true;
      })
      .map((r) => ({
        ...r,
        userRating: userRatings[r.id] || r.userRating || 0,
      }));
  }, [initialRestaurants, favoritesOnly, visitedFilter, userRatings]);

  // Use dynamic title based on total restaurants count if no custom title provided
  const displayTitle =
    title ||
    (totalRestaurants > 0
      ? `Explore Our ${totalRestaurants} Partners`
      : "Explore Our Partners");

  return (
    <section className="explore-partners">
      <ExploreHeader
        title={displayTitle}
        searchValue={searchValue}
        onSearchChange={onSearchChange}
        favoritesOnly={favoritesOnly}
        onToggleFavoritesOnly={() => setFavoritesOnly((v) => !v)}
        onOpenFilters={onOpenFilters}
        sortBy={sortBy}
        onSortChange={onSortChange}
        visitedFilter={visitedFilter}
        onVisitedFilterChange={onVisitedFilterChange}
      />

      <CategoryFilterBar
        categories={categories}
        activeCategory={activeCategory}
        onSelect={onCategoryChange}
        hasMore={categoriesHasMore}
        loadingMore={categoriesLoadingMore}
        onLoadMore={onLoadMoreCategories}
      />

      <div
        ref={scrollRef}
        onScroll={onLoadMore ? handleScroll : undefined}
        style={{ overflowY: "auto", maxHeight: "calc(100vh - 300px)" }}
      >
        <RestaurantGrid
          restaurants={visibleRestaurants}
          loading={loading}
          loadingMore={loadingMore}
          onToggleLike={handleToggleLike}
          onRedeem={handleRedeemFromCard}
          onSelectRestaurant={handleSelectRestaurant}
          loadingLikes={loadingLikes}
        />
        {/* Sentinel element for Intersection Observer infinite scroll */}
        {hasMore && (
          <div
            ref={sentinelRef}
            style={{ width: "1px", height: "1px", margin: "0 auto" }}
            aria-hidden="true"
          />
        )}
        {/* Load More button for large screens where content fits in viewport */}
        {showLoadMoreButton && (
          <div style={{ textAlign: "center", padding: "20px 0" }}>
            <SecondaryButton
              onClick={() => {
                setIsScrolling(true);
                onLoadMore();
                setTimeout(() => setIsScrolling(false), 500);
              }}
              loading={loadingMore}
            >
              Load More Restaurants
            </SecondaryButton>
          </div>
        )}
      </div>

      <RestaurantDetailModal
        restaurant={selectedRestaurant}
        isOpen={isModalOpen}
        onClose={toggleModal}
        onRedeem={handleRedeemFromDetailModal}
        onRatingChange={handleRatingChange}
        onRefreshRestaurant={handleRefreshRestaurant}
      />
    </section>
  );
};

ExplorePartnersSection.propTypes = {
  title: PropTypes.string,
  categories: PropTypes.array,
  loading: PropTypes.bool,
  loadingMore: PropTypes.bool,
  hasMore: PropTypes.bool,
  totalRestaurants: PropTypes.number,
  onLoadMore: PropTypes.func,
  restaurants: PropTypes.array,
  onSelectRestaurant: PropTypes.func,
  onRedeemRestaurant: PropTypes.func,
  onToggleLike: PropTypes.func,
  onRatingChanged: PropTypes.func,
  onOpenFilters: PropTypes.func,
  activeCategory: PropTypes.string.isRequired,
  onCategoryChange: PropTypes.func.isRequired,
  searchValue: PropTypes.string.isRequired,
  onSearchChange: PropTypes.func.isRequired,
  sortBy: PropTypes.string.isRequired,
  onSortChange: PropTypes.func.isRequired,
  categoriesHasMore: PropTypes.bool,
  categoriesLoadingMore: PropTypes.bool,
  onLoadMoreCategories: PropTypes.func,
  visitedFilter: PropTypes.string,
  onVisitedFilterChange: PropTypes.func,
};

ExplorePartnersSection.defaultProps = {
  title: null,
  categories: [],
  restaurants: [],
  loading: false,
  currentPage: 1,
  totalPages: 1,
  totalRestaurants: 0,
  onPageChange: () => {},
  onSelectRestaurant: () => {},
  onRedeemRestaurant: () => {},
  onOpenFilters: () => {},
  activeCategory: "all",
  onCategoryChange: () => {},
  searchValue: "",
  onSearchChange: () => {},
  sortBy: "",
  onSortChange: () => {},
  categoriesHasMore: false,
  categoriesLoadingMore: false,
  onLoadMoreCategories: () => {},
  visitedFilter: "all",
  onVisitedFilterChange: () => {},
};

export default ExplorePartnersSection;
export { ExploreHeader, CategoryFilterBar, RestaurantGrid, RestaurantCard };
